<template>
  <div class="tab-section filter-button-editor">
    <div class="section-heading">Filter buttons</div>
    <p class="section-desc">optional buttons on the view. visitors see the button name, not the rules. a button looks selected while it is on.</p>
    <label class="checkbox-label">
      <input type="checkbox" :checked="allowMultiple" @change="$emit('update:allow-multiple', $event.target.checked)" />
      allow more than one filter button at a time
    </label>
    <p class="section-desc">when this is off, choosing a button turns the previous one off. when this is on, every selected button applies, and a record has to match all of them.</p>

    <div v-if="!buttons.length" class="ffb-empty">No filter buttons configured</div>

    <div v-for="(button, index) in buttons" :key="button.id" class="ffb-card">
      <div class="ffb-card-head">
        <label class="ffb-label">
          button name
          <input
            type="text"
            class="ffb-input"
            :value="button.label"
            maxlength="80"
            placeholder="Button name"
            @input="updateLabel(index, $event.target.value)"
          />
        </label>
        <button type="button" class="ffb-remove" aria-label="Remove filter button" @click="removeButton(index)">x</button>
      </div>
      <p class="section-desc">these rules run when this button is selected. visitors do not see them.</p>
      <PreFilterBuilder
        :filters="button.filters || []"
        :match-mode="button.matchMode || 'all'"
        :spreadsheet="spreadsheet"
        :all-spreadsheets="allSpreadsheets"
        :application-unique-title="applicationUniqueTitle"
        empty-label="No rules for this button"
        @update:filters="updateFilters(index, $event)"
        @update:match-mode="updateMatchMode(index, $event)"
      />
    </div>

    <button type="button" class="ffb-add" @click="addButton">
      + add filter button
    </button>
  </div>
</template>

<script>
import PreFilterBuilder from './PreFilterBuilder.vue'
import { buttonIdFromLabel } from '../../../../shared/filter-buttons.js'

export default {
  name: 'FilterButtonEditor',
  components: { PreFilterBuilder },
  props: {
    buttons: { type: Array, default: () => [] },
    allowMultiple: { type: Boolean, default: false },
    spreadsheet: { type: Object, default: null },
    allSpreadsheets: { type: Array, default: () => [] },
    applicationUniqueTitle: { type: String, default: '' }
  },
  emits: ['update:buttons', 'update:allow-multiple'],
  methods: {
    replace (next) {
      this.$emit('update:buttons', next)
    },
    addButton () {
      const used = new Set(this.buttons.map((button) => button.id))
      const id = buttonIdFromLabel('filter', used)

      this.replace(this.buttons.concat([{
        id,
        label: '',
        filters: [],
        matchMode: 'all'
      }]))
    },
    removeButton (index) {
      this.replace(this.buttons.filter((_, buttonIndex) => buttonIndex !== index))
    },
    updateLabel (index, label) {
      this.replace(this.buttons.map((button, buttonIndex) => {
        if (buttonIndex !== index) return button
        return { ...button, label }
      }))
    },
    updateFilters (index, filters) {
      this.replace(this.buttons.map((button, buttonIndex) => {
        if (buttonIndex !== index) return button
        return { ...button, filters }
      }))
    },
    updateMatchMode (index, matchMode) {
      this.replace(this.buttons.map((button, buttonIndex) => {
        if (buttonIndex !== index) return button
        return { ...button, matchMode }
      }))
    }
  }
}
</script>

<style>
.filter-button-editor .ffb-empty {
  color: #6a6a67;
  font-style: italic;
  margin: 8px 0;
}

.filter-button-editor .ffb-card {
  border: 1px solid #333330;
  border-radius: 6px;
  padding: 12px;
  margin: 10px 0;
  background: #141412;
}

.filter-button-editor .ffb-card-head {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.filter-button-editor .ffb-label {
  flex: 1;
  color: #b7c96a;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.filter-button-editor .ffb-input {
  display: block;
  width: 100%;
  margin-top: 4px;
  background: #1a1a18;
  color: #e8e8e6;
  border: 1px solid #333330;
  border-radius: 4px;
  padding: 6px 8px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.95rem;
}

.filter-button-editor .ffb-remove {
  background: transparent;
  color: #a8a8a4;
  border: 1px solid #333330;
  border-radius: 4px;
  width: 28px;
  height: 32px;
  cursor: pointer;
}

.filter-button-editor .ffb-remove:hover {
  background: #f82471;
  border-color: #f82471;
  color: #fff;
}

.filter-button-editor .ffb-add {
  background: transparent;
  color: #b7c96a;
  border: 1px dashed #3d4a28;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
}
</style>
